import type { GridSortModel, GridFilterItem } from "@mui/x-data-grid"

import useSWR from "swr"
import axios from "axios"
import { toast } from "sonner"
import { useRef, useMemo, useState, useEffect } from "react"

import { httpClient as axiosInstance, fetcher, apiUrls } from "@/src/api"

import { swrOptions, getTableFilterString } from "./leads"

import type { ApiResponse } from "../types/apiResponse"
import type { FilterModel } from "../types/databaseSearchFilterModel"
import type { BulkUpload, BulkUploadTableFilters } from "../types/bulkUploadsModel"

// ----------------------------------------------------------------------

type BulkUploadData = {
    data: {
        content: BulkUpload[]
        totalElements: number | undefined
        bulkJobCountByStatus: {
            [key: string]: number
        }
    }
}

export function useGetBulkUploads(
    page: number,
    size: number,
    sortModel: GridSortModel,
    search: string,
    filterOptions: GridFilterItem[],
    extraFilters: BulkUploadTableFilters,
    enabled: boolean = true,
    isMagicLeads?: boolean
) {
    const url = apiUrls.BULK_UPLOAD_LIST()

    const filterString = getTableFilterString(sortModel, search, filterOptions, extraFilters)

    const swrKey = enabled
        ? `${url}?page=${page}&size=${size}${filterString}&bulk_job_type=${isMagicLeads ? "GET_MAGIC_LEADS" : "BULK_JOB"}`
        : null

    const { data, isLoading, error, isValidating, mutate } = useSWR<BulkUploadData>(swrKey, fetcher, swrOptions)

    const memoizedValue = useMemo(
        () => ({
            uploads: (data?.data?.content || []).map(mapUpload),
            uploadsCount: data?.data?.totalElements,
            uploadsLoading: isLoading,
            tabData: data?.data?.bulkJobCountByStatus,
            uploadsError: error,
            uploadsValidating: isValidating,
            uploadsEmpty: !isLoading && !data?.data?.content?.length,
            uploadsMutate: mutate,
        }),
        [data, error, isLoading, isValidating, mutate]
    )

    return memoizedValue
}

const mapUpload = (data: any): BulkUpload => ({
    ...data,
    createdBy: { label: data.createdBy?.name, value: data.createdBy?.id },
    mandate: { label: data.mandate?.name, value: data.mandate?.id, agentId: data.mandate?.agentId },
    recruiters: data.recruiters?.map((recruiter: any) => ({ label: recruiter.name, value: recruiter.id })) ?? [],
})

export async function uploadCsv(payload: any, fileName: string, fileType: string) {
    const url = `${apiUrls.BULK_UPLOAD_PRIVATE_FILE()}?fileName=${fileName}&fileType=${fileType}`
    try {
        const resp = await axiosInstance.get(url, payload)
        return {
            status: "success",
            data: resp?.data,
        }
    } catch (e: any) {
        console.log(e)
        return {
            status: "error",
            errorMessage: e.status,
        }
    }
}

export function uploadCsvFiles(url: string, payload: any) {
    const headers = {
        "Content-Type": "application/octet-stream",
    }
    return axios
        .put(url, payload, { headers })
        .then((resp): ApiResponse<void> => {
            return {
                status: "success",
                data: resp?.data,
            }
        })
        .catch((e): ApiResponse<void> => {
            console.log(e)
            return {
                status: "error",
                errorMessage: e.status,
            }
        })
}

/** Outbound bulk upload: roundId required; round must have outbound enabled. */
export type OutboundBulkUploadPayload = {
    jobId: string
    roundId: string
    fileUrl: string
}

export async function createUploadLeads(payload: any) {
    const url = apiUrls.BULK_UPLOAD_LEADS()
    try {
        const resp = await axiosInstance.post(url, payload)
        return {
            status: "success",
            data: resp?.data,
        }
    } catch (e: any) {
        console.log(e)
        return {
            status: "error",
            errorMessage: e.status ?? e.message,
        }
    }
}

export async function createMagicSourcing(payload: {
    mandateId: string
    recruiters: string[]
    cerebroFilters: FilterModel | null
}) {
    const url = apiUrls.BULK_UPLOAD_LEADS()
    try {
        const resp = await axiosInstance.post(url, {
            type: "GET_MAGIC_LEADS",
            metadata: payload,
            source: "GET_MAGIC_LEADS",
        })
        return {
            status: "success",
            data: resp?.data,
        }
    } catch (e: any) {
        console.log(e)
        return {
            status: "error",
            errorMessage: e.message ?? e.status,
        }
    }
}

/* export const updateClientForm = (payload: FormSchemaClientType) => {
    return axiosInstance.post(apiUrls.CLIENT_FORM(), payload)
}

export const editClientForm = (payload: FormSchemaClientType, id: string) => {
    return axiosInstance.put(`${apiUrls.CLIENT_FORM()}/${id}`, payload)
} */

export type BulkJobStatusValue = "REQUEST_ACCEPTED" | "PROCESSING" | "COMPLETED" | "FAILED"

export interface BulkJobStatusData {
    id: string
    status: BulkJobStatusValue
    success: number
    errors: number
    duplicates: number
    totalRecords: number
    reportUrl?: string
}

const BULK_JOB_PROGRESS_BY_STATUS: Record<BulkJobStatusValue, number> = {
    REQUEST_ACCEPTED: 15,
    PROCESSING: 60,
    COMPLETED: 100,
    FAILED: 100,
}

const BULK_JOB_TERMINAL_STATUSES = new Set<BulkJobStatusValue>(["COMPLETED", "FAILED"])

const delay = (milliseconds: number) => new Promise((resolve) => setTimeout(resolve, milliseconds))

export function extractBulkJobId(uploadResponse: { data?: { data?: { id?: string } } }): string | undefined {
    return uploadResponse?.data?.data?.id
}

export async function getBulkJobStatus(jobId: string): Promise<ApiResponse<BulkJobStatusData>> {
    const url = apiUrls.BULK_UPLOAD_JOB(jobId)
    try {
        const resp = await axiosInstance.get(url)
        if (resp?.data?.data?.status) {
            return {
                status: "success",
                data: resp.data.data,
            }
        }
        return {
            status: "error",
            errorMessage: "Failed to fetch bulk job status",
            data: resp?.data?.data,
        }
    } catch (error: unknown) {
        console.log(error)
        return {
            status: "error",
            errorMessage: "Failed to fetch bulk job status",
        }
    }
}

export async function pollBulkUploadJobUntilComplete(
    jobId: string,
    options?: {
        onProgress?: (progress: number, status: BulkJobStatusValue) => void
        pollIntervalMs?: number
        isCancelled?: () => boolean
    }
): Promise<ApiResponse<BulkJobStatusData>> {
    const pollIntervalMs = options?.pollIntervalMs ?? 2000

    while (true) {
        if (options?.isCancelled?.()) {
            return {
                status: "error",
                errorMessage: "Bulk upload polling cancelled",
            }
        }

        const statusResponse = await getBulkJobStatus(jobId)

        if (statusResponse.status === "success" && statusResponse.data) {
            const jobStatus = statusResponse.data.status
            options?.onProgress?.(BULK_JOB_PROGRESS_BY_STATUS[jobStatus] ?? 50, jobStatus)

            if (BULK_JOB_TERMINAL_STATUSES.has(jobStatus)) {
                return statusResponse
            }
        }

        await delay(pollIntervalMs)
    }
}

export async function waitForBulkJobCompletion(
    uploadResponse: { data?: { data?: { id?: string } } },
    onProgress: (progress: number) => void,
    isCancelled?: () => boolean
): Promise<BulkJobStatusData> {
    const bulkJobId = extractBulkJobId(uploadResponse)
    if (!bulkJobId) {
        throw new Error("Bulk job id not found")
    }

    const pollResult = await pollBulkUploadJobUntilComplete(bulkJobId, {
        onProgress: (progress) => onProgress(progress),
        isCancelled,
    })

    if (pollResult.status === "error" || !pollResult.data) {
        throw new Error(pollResult.errorMessage ?? "Bulk upload failed")
    }

    return pollResult.data
}

const pollBulkUpload = async (
    jobId: string
): Promise<ApiResponse<{ status: string; downloadUrl: string; comments: string; success: number }>> => {
    const statusResponse = await getBulkJobStatus(jobId)
    if (statusResponse.status === "success" && statusResponse.data) {
        return {
            status: "success",
            data: {
                ...statusResponse.data,
                downloadUrl: statusResponse.data.reportUrl ?? "",
                comments: "",
            },
        }
    }
    return {
        status: "error",
        errorMessage: "Uploading candidates failed",
        data: statusResponse.data as any,
    }
}

interface BulkUploadResp {
    percentage: number
}

export const useBulkUpload = (
    onComplete: (data: BulkUpload) => Promise<void>,
    suppressErrorToast?: boolean
): {
    bulkUploadResult: BulkUploadResp | undefined
    startBulkUpload: (exportStartRes: any) => void
} => {
    const jobId = useRef<string | null>()
    const pollTimer = useRef<number | null>()
    const [exportResult, setExportResult] = useState<BulkUploadResp | undefined>()

    const clearPollTimer = () => {
        jobId.current = null
        setExportResult(undefined)
        if (pollTimer.current) {
            clearTimeout(pollTimer.current)
            pollTimer.current = null
        }
    }

    useEffect(() => {
        clearPollTimer()
        return () => {
            clearPollTimer()
        }
    }, [])

    const pollExportStatus = async () => {
        if (!jobId.current) {
            clearPollTimer()
            return
        }

        const exportStatus = await pollBulkUpload(jobId.current)

        // Not handling failures of a single poll

        if (exportStatus.data?.status === "REQUEST_ACCEPTED") setExportResult({ percentage: 10 })
        else if (exportStatus.data?.status === "PROCESSING") setExportResult({ percentage: 50 })
        else if (exportStatus.data?.status === "COMPLETED") {
            setExportResult({ percentage: 99 })
            await onComplete(exportStatus.data as any)
            setExportResult(undefined)
            clearPollTimer()
            return
        } else if (exportStatus.data?.status === "FAILED") {
            await onComplete(exportStatus.data as any)
            if (!suppressErrorToast) toast.error("Failed to export candidates")
            clearPollTimer()
            return
        }
        setTimeout(pollExportStatus, 2000)
    }

    const startExport = async (exportStartRes: any) => {
        setExportResult({ percentage: 0 })
        jobId.current = exportStartRes.data.data.id
        pollExportStatus()
    }

    return { bulkUploadResult: exportResult, startBulkUpload: startExport }
}
